/**
 * Refund Form Slide-over Alpine.js Component
 *
 * Simple form for issuing refunds to landlords or contractors.
 * Credits the entity's main account and debits agency income + VAT.
 */

export function refundFormSlideover() {
  return {
    open: false,
    zIndex: 100010,

    // Form state
    netAmountPounds: '',
    vatRate: '20',
    description: '',

    // Source context
    source: null,
    entityName: '',

    // Submit state
    submitting: false,
    error: null,
    successMessage: null,

    openSlideover(detail) {
      const store = Alpine.store?.('slideovers')
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }

      this.resetForm()
      this.open = true
      this.source = detail?.source || null

      if (store?.register) {
        store.register('refund-form', 'new', this)
      }

      this.fetchEntityName()
    },

    close() {
      const store = Alpine.store?.('slideovers')
      if (store) {
        store.unregister('refund-form', 'new')
      }
      this.$nextTick(() => {
        this.open = false
      })
    },

    resetForm() {
      this.netAmountPounds = ''
      this.vatRate = '20'
      this.description = ''
      this.entityName = ''
      this.error = null
      this.successMessage = null
      this.submitting = false
    },

    async fetchEntityName() {
      if (!this.source) return

      const { type, id } = this.source
      const url = type === 'landlord'
        ? `/accounting/accounts/landlords/${id}.json`
        : `/accounting/accounts/contractors/${id}.json`

      try {
        const response = await fetch(url, {
          headers: { 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken }
        })
        if (response.ok) {
          const data = await response.json()
          this.entityName = data[type]?.name || `${type} #${id}`
        }
      } catch (e) {
        console.error('Failed to fetch entity name:', e)
      }
    },

    async submit() {
      if (!this.canSubmit || this.submitting) return

      this.submitting = true
      this.error = null
      this.successMessage = null

      const body = {
        refund: {
          type: this.source.type,
          id: this.source.id,
          net_amount_pence: this.netAmountPence,
          vat_amount_pence: this.vatAmountPence,
          description: this.description
        }
      }

      try {
        const response = await fetch('/accounting/accounts/refunds.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify(body)
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.error || 'Failed to create refund')
        }

        this.successMessage = 'Refund applied successfully'
        window.dispatchEvent(new CustomEvent('refund-created', { detail: data }))
        setTimeout(() => this.close(), 1500)
      } catch (e) {
        console.error('Failed to create refund:', e)
        this.error = e.message || 'Failed to apply refund. Please try again.'
      } finally {
        this.submitting = false
      }
    },

    // Computed
    get netAmountPence() {
      const pounds = parseFloat(this.netAmountPounds)
      if (isNaN(pounds) || pounds <= 0) return 0
      return Math.round(pounds * 100)
    },

    get vatAmountPence() {
      const rate = parseFloat(this.vatRate) || 0
      return Math.floor(this.netAmountPence * rate / 100)
    },

    get grossAmountPence() {
      return this.netAmountPence + this.vatAmountPence
    },

    get canSubmit() {
      if (!this.source) return false
      if (!this.description.trim()) return false
      if (this.netAmountPence <= 0) return false
      return true
    },

    get entityTypeLabel() {
      return this.source?.type === 'landlord' ? 'Landlord' : 'Contractor'
    },

    get csrfToken() {
      return document.querySelector('meta[name="csrf-token"]')?.content
    },

    formatPence(pence) {
      if (pence === null || pence === undefined) return '-'
      const absPence = Math.abs(pence)
      const pounds = (absPence / 100).toFixed(2)
      const formatted = parseFloat(pounds).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      return '\u00A3' + formatted
    }
  }
}
